import { useState, useEffect } from 'react';
import { jornadaControleAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Clock, Check, Lock, Loader2, AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const APROVADORES = ['ADMIN', 'GERENTE', 'SUPERVISOR'];

const HoraExtraApproval = () => {
  const { user } = useAuth();
  const [erro, setErro] = useState('');
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [senha, setSenha] = useState('');
  const [horas, setHoras] = useState('1');
  const [loading, setLoading] = useState(false);
  const [loadingReject, setLoadingReject] = useState(false);

  useEffect(() => {
    if (!user || !APROVADORES.includes(user.perfil)) return;
    
    const fetch = async () => {
      try {
        const res = await jornadaControleAPI.solicitacoesHoraExtra();
        const pendentes = res.data.filter(s => s.status === 'pendente');
        setSolicitacoes(pendentes);
      } catch (e) {}
    };
    
    fetch();
    const interval = setInterval(() => { if (!document.hidden) fetch(); }, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const handleReject = async () => {
    setLoadingReject(true);
    setErro('');
    try {
      const res = await jornadaControleAPI.negarHoraExtra({
        user_id: selected.user_id,
      });
      toast.info(res.data.message || 'Hora extra negada');
      setSolicitacoes(prev => prev.filter(s => s.user_id !== selected.user_id));
      setDialogOpen(false);
      setSelected(null);
    } catch (e) {
      setErro(e.response?.data?.detail || 'Erro ao negar hora extra');
    } finally {
      setLoadingReject(false);
    }
  };

  const handleApprove = async () => {
    if (!senha.trim()) {
      setErro('Digite sua senha');
      return;
    }
    if (!horas || parseFloat(horas) <= 0) {
      setErro('Informe as horas');
      return;
    }
    setLoading(true);
    setErro('');
    try {
      const res = await jornadaControleAPI.liberarHoraExtra({
        user_id: selected.user_id,
        horas: parseFloat(horas),
        senha: senha,
      });
      toast.success(res.data.message || 'Hora extra liberada!');
      setSolicitacoes(prev => prev.filter(s => s.user_id !== selected.user_id));
      setDialogOpen(false);
      setSenha('');
      setHoras('1');
      setSelected(null);
    } catch (e) {
      setErro(e.response?.data?.detail || 'Erro ao liberar hora extra');
    } finally {
      setLoading(false);
    }
  };

  if (!user || !APROVADORES.includes(user.perfil) || solicitacoes.length === 0) return null;

  return (
    <>
      {/* Botão flutuante com notificação */}
      <div className="fixed bottom-20 right-6 z-50 animate-bounce" data-testid="hora-extra-badge">
        <Button
          onClick={() => { setSelected(solicitacoes[0]); setHoras(String(solicitacoes[0].horas_solicitadas || 1)); setDialogOpen(true); }}
          className="bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-500/30 rounded-full px-5 py-3"
        >
          <Clock className="mr-2 h-5 w-5" />
          {solicitacoes.length} Hora Extra Pendente{solicitacoes.length > 1 ? 's' : ''}
        </Button>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Liberar Hora Extra
            </DialogTitle>
          </DialogHeader>
          
          {selected && (
            <div className="space-y-4">
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                <p className="text-sm text-gray-500 dark:text-gray-400">Colaborador</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">{selected.user_nome}</p>
                <p className="text-xs text-gray-400 mt-1">Solicitado em {new Date(selected.hora_solicitacao).toLocaleTimeString('pt-BR')}{selected.perfil ? ` · ${selected.perfil}` : ''}</p>
                {(selected.horas_solicitadas || selected.motivo) && (
                  <p className="text-sm mt-2 text-purple-800 dark:text-purple-200" data-testid="hora-extra-pedido-detalhe">
                    Pediu <b>{selected.horas_solicitadas || 1}h</b>{selected.motivo ? ` — "${selected.motivo}"` : ''}
                  </p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Quantas horas extras liberar?
                </label>
                <div className="flex gap-2">
                  {['0.5', '1', '2', '3'].map(h => (
                  <Button
                    key={h}
                    variant={horas === h ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setHoras(h)}
                    className={horas === h ? 'bg-purple-600' : ''}
                    data-testid={`hora-extra-${h}h`}
                  >
                    {h}h
                  </Button>
                ))}
                  <Input
                    type="number"
                    value={horas}
                    onChange={(e) => setHoras(e.target.value)}
                    className="w-20 text-center"
                    min="0.5"
                    max="8"
                    step="0.5"
                    data-testid="hora-extra-custom"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  <Lock className="inline h-4 w-4 mr-1" />
                  Confirme com a sua senha
                </label>
                <Input
                  type="password"
                  value={senha}
                  onChange={(e) => { setSenha(e.target.value); setErro(''); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleApprove()}
                  placeholder="Digite sua senha para confirmar"
                  data-testid="hora-extra-senha"
                  autoFocus
                />
              </div>

              {erro && <p className="text-red-500 text-sm">{erro}</p>}

              <div className="flex gap-2">
                <Button
                  onClick={handleReject}
                  disabled={loadingReject || loading}
                  variant="outline"
                  className="flex-1 border-red-500 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                  data-testid="hora-extra-negar-btn"
                >
                  {loadingReject ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <X className="mr-2 h-4 w-4" />}
                  Nao Autorizar
                </Button>
                <Button
                  onClick={handleApprove}
                  disabled={loading || loadingReject}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  data-testid="hora-extra-aprovar-btn"
                >
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                  Aprovar ({horas}h)
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HoraExtraApproval;
